import { Trans } from 'react-i18next';

import {
   CurrentHealthcareProviderEwusConfigurationQuery,
   CurrentHealthcareProviderEwusConfigurationSetCommand,
} from '@dreryk/egabinet-ewus-frontend-api';
import {
   CurrentHealthcareProviderNfzConfigurationQuery,
   CurrentHealthcareProviderNfzConfigurationSetCommand,
} from '@dreryk/egabinet-nfz-configuration-frontend-api';

import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice, Slot } from '@dreryk/watermelon';

import { nfzSettingsTranslationKeys } from '../../../translations';

import { NfzLoginLink } from './NfzLoginLink';
import { NfzConfigurationFormBean } from './NfzSettingsForm.types';

export const useCurrentHealthcareProviderNfzConfigurationSetCommand = (): UseCqrsCommandResult<
   void,
   NfzConfigurationFormBean
> => {
   return useCqrsCommand<void, NfzConfigurationFormBean>(
      (parameters) => {
         const command = new CurrentHealthcareProviderNfzConfigurationSetCommand();
         const nfzDepartment = parameters.nfzDepartment!;

         command.nfzDepartmentId = nfzDepartment.id;
         command.providerCode = Slot.emptyIfNull(parameters.providerCode ?? null);
         command.contractingPartyType = parameters.contractingPartyType ?? 'PERSON';

         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(nfzSettingsTranslationKeys.nfzIntegrationDataWasSaved);
            refetchCqrsQuery(CurrentHealthcareProviderNfzConfigurationQuery);
         },
      }
   );
};

const CONFIGURATION_SAVED_SNACKBAR_DELAY_IN_MS = 5000;
export const dispatchConfigurationSavePositiveSnackbar = (translationKey: string): void => {
   dispatchSnackbarPositiveEvent(
      <span>
         <Trans
            i18nKey={translationKey}
            components={{
               link: <NfzLoginLink testId={'nfzLogin'} />,
            }}
         />
      </span>,
      {
         autoHideDelayInMs: CONFIGURATION_SAVED_SNACKBAR_DELAY_IN_MS,
      }
   );
};

type CurrentHealthcareProviderEwusConfigurationParameters = {
   enabled: boolean;
};
export const useCurrentHealthcareProviderEwusConfigurationSetCommand = (): UseCqrsCommandResult<
   void,
   CurrentHealthcareProviderEwusConfigurationParameters
> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new CurrentHealthcareProviderEwusConfigurationSetCommand();
         if (parameters.enabled) {
            command.parameters = Choice.of({
               // Can't figure out why false needs to be casted, but is seems that sth's wrong with Choice
               automaticStatusCheck: false as boolean,
            });
         } else {
            command.parameters = Choice.withUseDefault();
         }

         return command;
      },
      {
         onSuccess: (__, parameters) => {
            dispatchConfigurationSavePositiveSnackbar(
               parameters.enabled
                  ? nfzSettingsTranslationKeys.ewusConfigurationWasSaved
                  : nfzSettingsTranslationKeys.ewusIntegrationTurnedOff
            );
            refetchCqrsQuery(CurrentHealthcareProviderEwusConfigurationQuery);
         },
      }
   );
};

type EwusAutomaticStatusCheckCommandParameter = {
   enabled: boolean;
};
export const useCurrentHealthcareProviderEwusAutomaticStatusCheckSetCommand = (
   onSuccess?: VoidFunction
): UseCqrsCommandResult<void, EwusAutomaticStatusCheckCommandParameter> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new CurrentHealthcareProviderEwusConfigurationSetCommand();
         command.parameters = Choice.of({
            automaticStatusCheck: parameters.enabled,
         });
         return command;
      },
      {
         onSuccess: (__, parameters) => {
            dispatchConfigurationSavePositiveSnackbar(
               parameters.enabled
                  ? nfzSettingsTranslationKeys.ewusConfigurationWasSaved
                  : nfzSettingsTranslationKeys.ewusAutomaticStatusCheckTurnedOff
            );
            onSuccess?.();
         },
      }
   );
};
